/* eslint-disable prettier/prettier */
import { Box, Button, Group, Stepper, Text, LoadingOverlay } from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";
import { showNotification } from "@mantine/notifications";
import { IconCheck, IconBuilding, IconUser, IconSettings, IconBuildingCommunity } from "@tabler/icons-react";
import { useState, useCallback, useRef, useEffect } from "react";
import { DOIS, QUATRO, TRES } from "@/utils/constants";
import { useCnpjLookup } from "./hooks/useCnpjLookup";
import { LicenciadaInfoStep, EmpresaInfoStep, TemplateSelectionStep, AdminUserStep } from "./steps";
import { useCreateLicenciada } from "../../hooks/useCreateLicenciada";
import { createLicenciadaSchema, CreateLicenciadaFormData } from "../../schema/create-licenciada-schema";

interface CreateLicenciadaFormProperties {
  onSuccess: () => void;
  onCancel: () => void;
}

const CreateLicenciadaForm = ({ onSuccess, onCancel }: CreateLicenciadaFormProperties) => {
  const [active, setActive] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const createLicenciada = useCreateLicenciada();
  const formContainerReference = useRef<HTMLDivElement>(null);

  const form = useForm<CreateLicenciadaFormData>({
    validate: yupResolver(createLicenciadaSchema),
    initialValues: {
      novaLicenciada: {
        cnpj: "",
        razaoSocial: "",
        nomeFantasia: "",
        email: "",
        telefone: "",
        status: "ATIVO",
        contratoInicio: new Date(),
        contratoFim: new Date(),
        endereco: {
          cep: "",
          logradouro: "",
          numero: "",
          complemento: "",
          bairro: "",
          estado: "",
          cidade: ""
        },
        acessos: {
          admin: 0,
          member: 0,
          team: 0,
          master: 0,
          collaborator: 0
        },
        autorizacoes: {
          gestao: false,
          diagnostico: false,
          incentivo: false,
          configuracoes: true
        }
      },
      novaEmpresa: {
        cnpj: "",
        razaoSocial: "",
        nomeFantasia: "",
        email: "",
        telefone: "",
        codigoCNAE: "",
        cnae: "",
        porte: "",
        setores: [],
        status: "ATIVO",
        numeroColaboradores: "",
        responsavel: {
          nome: "",
          email: "",
          telefone: ""
        },
        colaboradores: "",
        endereco: {
          cep: "",
          logradouro: "",
          numero: "",
          complemento: "",
          bairro: "",
          estado: "",
          cidade: ""
        },
        role: "NORMAL"
      },
      template: {
        licenciadaId: "",
        empresaId: "",
        collections: []
      },
      admin: {
        nome: "",
        email: "",
        telefone: ""
      }
    }
  });

  const { isFetching: isFetchingLicenciadaCnpj } = useCnpjLookup(form, "novaLicenciada");
  const { isFetching: isFetchingEmpresaCnpj } = useCnpjLookup(form, "novaEmpresa");

  // Scroll ao topo e foco quando mudar de step
  useEffect(() => {
    if (formContainerReference.current) {
      // Scroll suave ao topo do formulário
      formContainerReference.current.scrollIntoView({ behavior: "smooth", block: "start" });

      // Alternativa: scroll da janela ao topo
      window.scrollTo({ top: 0, behavior: "smooth" });

      // Foca no primeiro input do step após um pequeno delay
      setTimeout(() => {
        const firstInput = formContainerReference.current?.querySelector<HTMLInputElement>(
          'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled])'
        );
        firstInput?.focus();
      }, 300);
    }
  }, [active]);

  const getStepFields = (step: number): string[] => {
    switch (step) {
      case 0: {
        return [
          "novaLicenciada.cnpj",
          "novaLicenciada.razaoSocial",
          "novaLicenciada.nomeFantasia",
          "novaLicenciada.email",
          "novaLicenciada.telefone",
          "novaLicenciada.contratoInicio",
          "novaLicenciada.contratoFim",
          "novaLicenciada.endereco.cep",
          "novaLicenciada.endereco.logradouro",
          "novaLicenciada.endereco.numero",
          "novaLicenciada.endereco.bairro",
          "novaLicenciada.endereco.estado",
          "novaLicenciada.endereco.cidade",
          "novaLicenciada.acessos.admin",
          "novaLicenciada.acessos.member",
          "novaLicenciada.acessos.team",
          "novaLicenciada.acessos.master",
          "novaLicenciada.acessos.collaborator",
          "novaLicenciada.autorizacoes.gestao",
          "novaLicenciada.autorizacoes.diagnostico",
          "novaLicenciada.autorizacoes.incentivo",
          "novaLicenciada.autorizacoes.configuracoes"
        ];
      }
      case 1: {
        return [
          "novaEmpresa.cnpj",
          "novaEmpresa.razaoSocial",
          "novaEmpresa.nomeFantasia",
          "novaEmpresa.email",
          "novaEmpresa.telefone",
          "novaEmpresa.codigoCNAE",
          "novaEmpresa.cnae",
          "novaEmpresa.porte",
          "novaEmpresa.numeroColaboradores",
          "novaEmpresa.setores",
          "novaEmpresa.endereco.cep",
          "novaEmpresa.endereco.logradouro",
          "novaEmpresa.endereco.numero",
          "novaEmpresa.endereco.bairro",
          "novaEmpresa.endereco.estado",
          "novaEmpresa.endereco.cidade",
          "novaEmpresa.responsavel.nome",
          "novaEmpresa.responsavel.email",
          "novaEmpresa.responsavel.telefone"
        ];
      }
      case DOIS: {
        return ["template.empresaId"];
      }
      case TRES: {
        return ["admin.nome", "admin.email", "admin.telefone"];
      }
      default: {
        return [];
      }
    }
  };

  const nextStep = () => {
    const errors = form.validate();

    const stepFields = getStepFields(active);
    const stepErrors: Record<string, string> = {};

    for (const field of stepFields) {
      if (errors.errors[field]) {
        stepErrors[field] = errors.errors[field] as string;
      }
    }

    if (Object.keys(stepErrors).length > 0) {
      const errorMessages = Object.entries(stepErrors)
        .map(([field]) => {
          const fieldName = getFieldDisplayName(field);
          return `• ${fieldName}`;
        })
        .join("\n");

      showNotification({
        title: "Campos obrigatórios",
        message: `Por favor, preencha os seguintes campos:\n${errorMessages}`,
        color: "red",
        autoClose: 4000
      });

      return;
    }

    const allErrors = { ...errors.errors };
    for (const field of Object.keys(allErrors)) {
      if (!stepFields.includes(field)) {
        form.clearFieldError(field);
      }
    }

    if (active < TRES) {
      // Adiciona pequeno delay visual para transição suave
      setIsTransitioning(true);
      setTimeout(() => {
        setActive(active + 1);
        setIsTransitioning(false);
      }, 200);
    }
  };

  const previousStep = useCallback(() => {
    const newActive = Math.max(active - 1, 0);
    const previousStepFields = getStepFields(newActive);

    const currentErrors = form.errors;
    for (const field of Object.keys(currentErrors)) {
      if (!previousStepFields.includes(field)) {
        form.clearFieldError(field);
      }
    }

    // Adiciona pequeno delay visual para transição suave
    setIsTransitioning(true);
    setTimeout(() => {
      setActive(newActive);
      setIsTransitioning(false);
    }, 200);
  }, [active]);

  const handleValidateAndSubmit = useCallback(() => {
    // Copia os dados do admin para o responsável ANTES da validação
    form.setFieldValue("novaLicenciada.responsavel", {
      nome: form.values.admin.nome,
      email: form.values.admin.email,
      telefone: form.values.admin.telefone
    });

    // Aguarda um momento para o form atualizar e então valida
    setTimeout(() => {
      const errors = form.validate();

      if (errors.hasErrors) {
        console.error("Errors found:", errors.errors);

        const errorMessages = Object.entries(errors.errors)
          .map(([field, message]) => {
            const fieldName = getFieldDisplayName(field);
            return `• ${fieldName}: ${String(message)}`;
          })
          .join("\n");

        showNotification({
          title: "Erro de validação",
          message: `Por favor, corrija os seguintes campos:\n${errorMessages}`,
          color: "red",
          autoClose: 5000
        });
        return;
      }

      handleSubmit();
    }, 0);
  }, [form]);

  const handleSubmit = form.onSubmit((values) => {
    // Copia os dados do admin para o responsável da licenciada
    const dataToSubmit = {
      ...values,
      novaLicenciada: {
        ...values.novaLicenciada,
        responsavel: {
          nome: values.admin.nome,
          email: values.admin.email,
          telefone: values.admin.telefone
        }
      },
      template: {
        ...values.template,
        licenciadaId: values.template.licenciadaId || ""
      }
    };

    createLicenciada.mutate(dataToSubmit, {
      onSuccess: () => {
        form.reset();
        onSuccess();
      },
      onError: (error) => {
        console.error("ERROR:", error);
      }
    });
  });

  const handleBackClick = useCallback(() => {
    if (active === 0) {
      onCancel();
    } else {
      previousStep();
    }
  }, [active, onCancel, previousStep]);

  const handleNextClick = useCallback(() => {
    if (active === TRES) {
      handleValidateAndSubmit();
    } else {
      nextStep();
    }
  }, [active, handleValidateAndSubmit, nextStep]);

  return (
    <Box pos="relative" ref={formContainerReference}>
      <LoadingOverlay visible={createLicenciada.isPending || isTransitioning} />

      <form onSubmit={handleSubmit}>
        <Stepper active={active} allowNextStepsSelect={false}>
          <Stepper.Step
            label="Licenciada"
            description="Dados da licenciada"
            icon={<IconBuilding size="1.1rem" />}
            completedIcon={<IconCheck size="1.1rem" />}
          >
            <LicenciadaInfoStep form={form} isFetchingCnpj={isFetchingLicenciadaCnpj} />
          </Stepper.Step>

          <Stepper.Step
            label="Empresa"
            description="Primeira empresa"
            icon={<IconBuildingCommunity size="1.1rem" />}
            completedIcon={<IconCheck size="1.1rem" />}
          >
            <EmpresaInfoStep form={form} isFetchingCnpj={isFetchingEmpresaCnpj} />
          </Stepper.Step>

          <Stepper.Step
            label="Modelo"
            description="Empresa para clonagem"
            icon={<IconSettings size="1.1rem" />}
            completedIcon={<IconCheck size="1.1rem" />}
          >
            <TemplateSelectionStep form={form} />
          </Stepper.Step>

          <Stepper.Step
            label="Admin"
            description="Usuário administrador"
            icon={<IconUser size="1.1rem" />}
            completedIcon={<IconCheck size="1.1rem" />}
          >
            <AdminUserStep form={form} />
          </Stepper.Step>

          <Stepper.Completed>
            <Text ta="center" mt="md">
              Todos os dados foram preenchidos. Clique em &quot;Criar Licenciada&quot; para processar.
            </Text>
          </Stepper.Completed>
        </Stepper>

        <Group justify="space-between" mt="xl">
          <Button variant="default" onClick={handleBackClick}>
            {active === 0 ? "Cancelar" : "Voltar"}
          </Button>

          {active === QUATRO ? null : (
            <Button onClick={handleNextClick} loading={createLicenciada.isPending}>
              {active === TRES ? "Criar Licenciada" : "Próximo"}
            </Button>
          )}
        </Group>
      </form>
    </Box>
  );
};

const getFieldDisplayName = (fieldPath: string): string => {
  const fieldMap: Record<string, string> = {
    cnpj: "CNPJ",
    razaoSocial: "Razão Social",
    nomeFantasia: "Nome Fantasia",
    email: "Email",
    telefone: "Telefone",
    contratoInicio: "Início do Contrato",
    contratoFim: "Fim do Contrato",
    cep: "CEP",
    logradouro: "Logradouro",
    numero: "Número",
    bairro: "Bairro",
    estado: "Estado",
    cidade: "Cidade",
    complemento: "Complemento",
    nome: "Nome",
    codigoCNAE: "Código CNAE",
    cnae: "CNAE",
    porte: "Porte",
    numeroColaboradores: "Número de Colaboradores",
    setores: "Setores",
    licenciadaId: "Licenciada Modelo",
    empresaId: "Empresa Modelo",
    status: "Status",
    gestao: "Gestão",
    diagnostico: "Diagnóstico",
    incentivo: "Incentivo",
    configuracoes: "Configurações"
  };

  const fieldName = fieldPath.split(".").pop() || fieldPath;
  return fieldMap[fieldName] || fieldName;
};

export default CreateLicenciadaForm;
